/**
 * ManageServices.jsx
 * Admin CRUD for Services Management with Image Upload
 */

import { useState, useEffect } from "react";
import {
  BSModal,
  DeleteModal,
  Field,
  PageHeader,
  Loading,
  EmptyState,
  apiFetch,
} from "../../components/helpers";

const EMPTY_FORM = {
  service_id: null,
  service_name: "",
  description: "",
  price: "",
  image: null,
  old_image: "",
};

export default function ManageServices() {
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [imagePreview, setImagePreview] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const json = await apiFetch(
        `${process.env.REACT_APP_GET_ALL_SERVICES}`
      );
      if (json.status) {
        setServices(json.data || []);
      } else {
        setServices([]);
      }
    } catch {
      setServices([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setImagePreview("");
    setError("");
    setModalOpen(true);
  };

  const openEdit = (service) => {
    setForm({
      ...service,
      price: service.price.toString(),
      image: null,
      old_image: service.image || "",
    });
    setImagePreview(service.image ? imgUrl(service.image) : "");
    setError("");
    setModalOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm({ ...form, image: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!form.service_name.trim()) {
      return setError("Service name is required");
    }
    if (!form.description.trim()) {
      return setError("Description is required");
    }
    if (!form.price || isNaN(form.price) || parseFloat(form.price) <= 0) {
      return setError("Price is required and must be a positive number");
    }

    setSubmitting(true);
    setError("");
    try {
      const isEdit = !!form.service_id;

      const fd = new FormData();
      fd.append("service_id", form.service_id || "");
      fd.append("service_name", form.service_name.trim());
      fd.append("description", form.description.trim());
      fd.append("price", parseFloat(form.price));
      fd.append("old_image", form.old_image || "");

      if (form.image) {
        fd.append("image", form.image);
      }

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}${
          isEdit
            ? process.env.REACT_APP_UPDATE_SERVICE
            : process.env.REACT_APP_ADD_SERVICE
        }`,
        {
          method: "POST",
          body: fd,
        }
      );

      const json = await res.json();

      if (json.status) {
        setModalOpen(false);
        setForm(EMPTY_FORM);
        setImagePreview("");
        setSuccess(
          isEdit ? "Service updated successfully!" : "Service added successfully!"
        );
        load();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(json.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      setError("Network error");
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    try {
      const json = await apiFetch(
        `${process.env.REACT_APP_DELETE_SERVICE}`,
        {
          method: "DELETE",
          body: { service_id: deleteId },
        }
      );

      if (json.status) {
        setDeleteId(null);
        load();
      } else {
        alert(json.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const imgUrl = (img) => {
    if (!img) return "";
    let clean = img.replace(/\\/g, "/").trim();
    clean = clean.replace(/^omdentalclinic\//, "");
    if (clean.startsWith("http://") || clean.startsWith("https://")) {
      return clean;
    }
    const baseUrl = process.env.REACT_APP_WEB_URL || "http://localhost/omdentalclinic";
    return `${baseUrl}/${clean.replace(/^\/+/, "")}`;
  };

  const filtered = services.filter((s) =>
    s.service_name?.toLowerCase().includes(search.toLowerCase()) ||
    s.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div
      className="p-3 p-lg-4"
      style={{ minHeight: "100vh", fontFamily: "'Nunito','Segoe UI',sans-serif" }}
    >
      <PageHeader
        title="Manage Services"
        subtitle={`${filtered.length} service${filtered.length !== 1 ? "s" : ""} found`}
      >
        <input
          className="form-control"
          placeholder="Search services..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 220, fontSize: 13 }}
        />
        <button
          className="btn fw-bold text-white"
          style={{ background: "#1D9E75", borderRadius: 10 }}
          onClick={openAdd}
        >
          + Add Service
        </button>
      </PageHeader>

      {success && (
        <div
          className="alert alert-success py-2 px-3 mb-3"
          style={{ fontSize: 13 }}
        >
          {success}
        </div>
      )}

      <div
        className="card border-0 shadow-sm"
        style={{ borderRadius: 16 }}
      >
        <div className="card-body p-0">
          {loading ? (
            <Loading />
          ) : filtered.length === 0 ? (
            <EmptyState message="No services found." />
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ background: "#FAFBFC" }}>
                  <tr>
                    {["Service Name", "Description", "Image", "Price (₹)", "Actions"].map(
                      (h) => (
                        <th
                          key={h}
                          className="text-muted fw-bold border-0 px-4 py-3"
                          style={{
                            fontSize: 10,
                            letterSpacing: ".6px",
                            textTransform: "uppercase",
                          }}
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((service) => (
                    <tr key={service.service_id}>
                      <td className="px-4">
                        <div className="fw-semibold" style={{ fontSize: 13 }}>
                          {service.service_name}
                        </div>
                      </td>
                      <td className="px-4 text-muted" style={{ fontSize: 13 }}>
                        {service.description?.length > 50
                          ? `${service.description.substring(0, 50)}...`
                          : service.description}
                      </td>
                      <td className="px-4">
                        {service.image ? (
                          <img
                            src={imgUrl(service.image)}
                            alt={service.service_name}
                            style={{
                              width: 60,
                              height: 60,
                              borderRadius: 8,
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <span
                            className="badge text-bg-secondary"
                            style={{ fontSize: 11 }}
                          >
                            No Image
                          </span>
                        )}
                      </td>
                      <td className="px-4 fw-bold" style={{ fontSize: 13 }}>
                        ₹{parseFloat(service.price).toLocaleString("en-IN")}
                      </td>
                      <td className="px-4">
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm fw-bold"
                            style={{
                              background: "#87bae9",
                              color: "#042C53",
                              fontSize: 11,
                            }}
                            onClick={() => openEdit(service)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm fw-bold"
                            style={{
                              background: "#f1b6b6",
                              color: "#501313",
                              fontSize: 11,
                            }}
                            onClick={() => setDeleteId(service.service_id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit Modal */}
      <BSModal
        open={modalOpen}
        title={form.service_id ? "Edit Service" : "Add Service"}
        onClose={() => setModalOpen(false)}
        size="modal-lg"
      >
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <Field label="Service Name">
              <input
                className="form-control"
                value={form.service_name}
                onChange={(e) =>
                  setForm({ ...form, service_name: e.target.value })
                }
                placeholder="e.g. Root Canal Treatment"
              />
            </Field>
          </div>
          <div className="col-12 col-md-6">
            <Field label="Price (₹)">
              <input
                className="form-control"
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="e.g. 5000"
                step="0.01"
                min="0"
              />
            </Field>
          </div>
          <div className="col-12">
            <Field label="Description">
              <textarea
                className="form-control"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="Provide a detailed description of the service..."
                rows="4"
              />
            </Field>
          </div>
          <div className="col-12 col-md-6">
            <Field label="Upload Image">
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={handleImageChange}
              />
              <small className="text-muted d-block mt-1">
                Max 5MB. JPEG, PNG, GIF, WebP only
              </small>
            </Field>
          </div>
          {imagePreview && (
            <div className="col-12 col-md-6">
              <Field label="Image Preview">
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    width: "100%",
                    height: 120,
                    borderRadius: 8,
                    objectFit: "cover",
                    border: "2px solid #ddd",
                  }}
                />
              </Field>
            </div>
          )}
        </div>
        {error && (
          <div
            className="alert alert-danger py-2 px-3 mt-2 mb-0"
            style={{ fontSize: 12 }}
          >
            {error}
          </div>
        )}
        <div className="d-flex gap-2 justify-content-end mt-3">
          <button
            className="btn btn-light fw-semibold"
            onClick={() => {
              setModalOpen(false);
              setForm(EMPTY_FORM);
              setImagePreview("");
            }}
          >
            Cancel
          </button>
          <button
            className="btn fw-bold text-white"
            style={{ background: "#1D9E75" }}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />
                Saving...
              </>
            ) : (
              "Save Service"
            )}
          </button>
        </div>
      </BSModal>

      <DeleteModal
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        message="Deleting a service cannot be undone."
      />
    </div>
  );
}